import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { listScheduledMessages, listRecurrenceDayPatterns } from '../../../api/scheduledMessages';
import { listScheduledMessagesAdmin, listRecurrenceDayPatternsAdmin } from '../../../api/scheduledMessagesAdmin';
import { getApiErrorMessage } from '../../../utils/errors';
import AgentFilterSelect from './AgentFilterSelect';
import CreateScheduledMessageForm from './CreateScheduledMessageForm';
import ScheduledMessagesTable from './ScheduledMessagesTable';
import { PER_PAGE, RECURRENCE_DAYS_FILTER_PREFIX, STATUS_FILTER_OPTIONS } from '../constants';
import {
  buildRecurrenceFilterOptions,
  loadContactsForItems,
  parseRecurrenceFilterValue,
  shouldPollItems,
} from '../utils';

const CONTACT_FILTER_DEBOUNCE_MS = 300;

function ScheduledMessagesPage({ mode }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialAgentId = searchParams.get('agentId') || '';
  const initialStatus = searchParams.get('status') || '';
  const initialContact = searchParams.get('contact') || '';
  const initialRecurrencePreset = searchParams.get('recurrencePreset') || '';
  const initialRecurrenceDays = searchParams.get('recurrenceDays') || '';
  const initialRecurrenceFilter = initialRecurrenceDays
    ? `${RECURRENCE_DAYS_FILTER_PREFIX}${initialRecurrenceDays}`
    : initialRecurrencePreset;

  const [selectedAgentId, setSelectedAgentId] = useState(initialAgentId);
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [recurrenceFilter, setRecurrenceFilter] = useState(initialRecurrenceFilter);
  const [recurrencePatternOptions, setRecurrencePatternOptions] = useState([]);
  const [contactFilter, setContactFilter] = useState(initialContact);
  const [debouncedContactFilter, setDebouncedContactFilter] = useState(initialContact.trim());
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ page: 1, per_page: PER_PAGE, total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [contactsById, setContactsById] = useState({});
  const pollRef = useRef(null);
  const contactsByIdRef = useRef({});
  contactsByIdRef.current = contactsById;

  const requiresAgent = mode === 'user';
  const showAgentColumn = mode === 'admin' && !selectedAgentId;
  const recurrenceFilterParams = useMemo(
    () => parseRecurrenceFilterValue(recurrenceFilter),
    [recurrenceFilter]
  );

  const recurrenceFilterOptions = useMemo(
    () => buildRecurrenceFilterOptions(recurrencePatternOptions),
    [recurrencePatternOptions]
  );

  const loadRecurrencePatterns = useCallback(async () => {
    if (requiresAgent && !selectedAgentId) {
      setRecurrencePatternOptions([]);
      return;
    }

    try {
      const response = mode === 'admin'
        ? await listRecurrenceDayPatternsAdmin({ agentId: selectedAgentId || null })
        : await listRecurrenceDayPatterns(selectedAgentId);
      setRecurrencePatternOptions(response.data || []);
    } catch {
      setRecurrencePatternOptions([]);
    }
  }, [mode, requiresAgent, selectedAgentId]);

  useEffect(() => {
    loadRecurrencePatterns();
  }, [loadRecurrencePatterns]);

  const filtersDisabled = loading || (requiresAgent && !selectedAgentId);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedContactFilter(contactFilter.trim());
    }, CONTACT_FILTER_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [contactFilter]);

  const loadContactsForPageItems = useCallback(async (pageItems) => {
    const map = await loadContactsForItems(pageItems, contactsByIdRef.current);
    contactsByIdRef.current = map;
    setContactsById(map);
  }, []);

  const loadItems = useCallback(
    async (page = 1, { silent = false } = {}) => {
      if (requiresAgent && !selectedAgentId) {
        setItems([]);
        setMeta({ page: 1, per_page: PER_PAGE, total: 0 });
        setLoading(false);
        return;
      }

      if (!silent) {
        setLoading(true);
        setError('');
      }

      try {
        let response;
        if (mode === 'admin') {
          response = await listScheduledMessagesAdmin({
            page,
            perPage: PER_PAGE,
            status: statusFilter || null,
            agentId: selectedAgentId || null,
            search: debouncedContactFilter || null,
            recurrencePreset: recurrenceFilterParams.recurrencePreset,
            recurrenceDays: recurrenceFilterParams.recurrenceDays,
          });
        } else {
          response = await listScheduledMessages(selectedAgentId, {
            page,
            perPage: PER_PAGE,
            status: statusFilter || null,
            search: debouncedContactFilter || null,
            recurrencePreset: recurrenceFilterParams.recurrencePreset,
            recurrenceDays: recurrenceFilterParams.recurrenceDays,
          });
        }
        const pageItems = response.data || [];
        setItems(pageItems);
        setMeta(response.meta || { page, per_page: PER_PAGE, total: 0 });
        if (mode !== 'admin') {
          await loadContactsForPageItems(pageItems);
        }
      } catch (err) {
        if (!silent) {
          setError(getApiErrorMessage(err, 'Não foi possível carregar os agendamentos.'));
          setItems([]);
        }
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [mode, requiresAgent, selectedAgentId, statusFilter, recurrenceFilterParams, debouncedContactFilter, loadContactsForPageItems]
  );

  useEffect(() => {
    setSelectedAgentId(initialAgentId);
    setStatusFilter(initialStatus);
    setRecurrenceFilter(initialRecurrenceFilter);
  }, [initialAgentId, initialStatus, initialRecurrenceFilter]);

  useEffect(() => {
    loadItems(1);
  }, [loadItems]);

  useEffect(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (shouldPollItems(items)) {
      pollRef.current = setInterval(() => {
        loadItems(meta.page, { silent: true });
      }, 8000);
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
      }
    };
  }, [items, loadItems, meta.page]);

  const syncSearchParams = useCallback(
    (agentId, status, contact, recurrenceValue) => {
      const next = new URLSearchParams();
      if (agentId) {
        next.set('agentId', agentId);
      }
      if (status) {
        next.set('status', status);
      }
      const { recurrencePreset, recurrenceDays } = parseRecurrenceFilterValue(recurrenceValue);
      if (recurrenceDays) {
        next.set('recurrenceDays', recurrenceDays);
      } else if (recurrencePreset) {
        next.set('recurrencePreset', recurrencePreset);
      }
      if (contact) {
        next.set('contact', contact);
      }
      setSearchParams(next, { replace: true });
    },
    [setSearchParams]
  );

  useEffect(() => {
    syncSearchParams(
      selectedAgentId,
      statusFilter,
      debouncedContactFilter,
      recurrenceFilter
    );
  }, [debouncedContactFilter, selectedAgentId, statusFilter, recurrenceFilter, syncSearchParams]);

  const handleAgentChange = (event) => {
    const agentId = event.target.value;
    setSelectedAgentId(agentId);
    syncSearchParams(agentId, statusFilter, debouncedContactFilter, recurrenceFilter);
  };

  const handleStatusChange = (event) => {
    const status = event.target.value;
    setStatusFilter(status);
    syncSearchParams(selectedAgentId, status, debouncedContactFilter, recurrenceFilter);
  };

  const handleRecurrenceFilterChange = (event) => {
    const nextRecurrenceFilter = event.target.value;
    setRecurrenceFilter(nextRecurrenceFilter);
    syncSearchParams(selectedAgentId, statusFilter, debouncedContactFilter, nextRecurrenceFilter);
  };

  const handleContactChange = (event) => {
    setContactFilter(event.target.value);
  };

  const handleClearFilters = () => {
    setStatusFilter('');
    setRecurrenceFilter('');
    setContactFilter('');
    setDebouncedContactFilter('');
    syncSearchParams(selectedAgentId, '', '', '');
  };

  const activeFilterCount =
    (statusFilter ? 1 : 0) +
    (recurrenceFilter ? 1 : 0) +
    (contactFilter.trim() ? 1 : 0);
  const hasActiveFilters = activeFilterCount > 0;

  const totalPages = Math.max(1, Math.ceil(meta.total / meta.per_page));
  const hasPagination = meta.total > meta.per_page;

  const subtitle = useMemo(() => {
    if (mode === 'admin') {
      return 'Visão global de mensagens proativas agendadas via WhatsApp.';
    }
    return 'Agende mensagens proativas geradas pelo agente para contatos do WhatsApp.';
  }, [mode]);

  const emptyMessage = useMemo(() => {
    if (requiresAgent && !selectedAgentId) {
      return {
        title: 'Selecione um agente',
        description: 'Escolha um agente acima para listar os agendamentos.',
      };
    }
    return {
      title: 'Nenhum agendamento encontrado',
      description: selectedAgentId
        ? 'Este agente ainda não possui mensagens agendadas.'
        : 'Quando agendamentos forem criados, eles aparecerão aqui.',
    };
  }, [requiresAgent, selectedAgentId]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-content-primary">Mensagens agendadas</h1>
          <p className="text-content-muted text-sm mt-1">{subtitle}</p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {!loading && meta.total > 0 && (
            <p className="text-sm text-content-subtle">
              {meta.total} agendamento{meta.total !== 1 ? 's' : ''}
            </p>
          )}
          {!showCreateForm && (
            <button
              type="button"
              onClick={() => setShowCreateForm(true)}
              className="btn-primary text-sm px-4 py-2"
            >
              Novo agendamento
            </button>
          )}
        </div>
      </div>

      {showCreateForm && (
        <CreateScheduledMessageForm
          mode={mode}
          onSuccess={(createdAgentId) => {
            setShowCreateForm(false);
            if (createdAgentId) {
              if (createdAgentId !== selectedAgentId) {
                setSelectedAgentId(createdAgentId);
                syncSearchParams(
                  createdAgentId,
                  statusFilter,
                  debouncedContactFilter,
                  recurrenceFilter
                );
              } else {
                loadItems(1);
                loadRecurrencePatterns();
              }
            } else {
              loadRecurrencePatterns();
            }
          }}
          onClose={() => setShowCreateForm(false)}
        />
      )}

      <div className="surface-card p-4">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-content-primary">Filtros</h2>
            {activeFilterCount > 0 && (
              <span className="inline-flex items-center justify-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-content-muted">
                {activeFilterCount}
              </span>
            )}
          </div>
          {hasActiveFilters && (
            <button
              type="button"
              onClick={handleClearFilters}
              disabled={loading}
              className="text-xs font-medium text-content-muted hover:text-content-primary disabled:opacity-40"
            >
              Limpar filtros
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-x-4 gap-y-3">
          <AgentFilterSelect
            value={selectedAgentId}
            onChange={handleAgentChange}
            mode={mode}
            disabled={loading}
            layout="stacked"
          />

          <div className="min-w-0">
            <label htmlFor="status-filter" className="block text-sm font-medium text-slate-700 mb-2">
              Status
            </label>
            <select
              id="status-filter"
              value={statusFilter}
              onChange={handleStatusChange}
              disabled={filtersDisabled}
              className="field-input w-full disabled:opacity-60"
            >
              {STATUS_FILTER_OPTIONS.map((option) => (
                <option key={option.value || 'all'} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="min-w-0">
            <label htmlFor="recurrence-preset-filter" className="block text-sm font-medium text-slate-700 mb-2">
              Padrão de recorrência
            </label>
            <select
              id="recurrence-preset-filter"
              value={recurrenceFilter}
              onChange={handleRecurrenceFilterChange}
              disabled={filtersDisabled}
              className="field-input w-full disabled:opacity-60"
            >
              {recurrenceFilterOptions.map((option) => (
                <option key={option.value || 'all-presets'} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="min-w-0">
            <label htmlFor="contact-filter" className="block text-sm font-medium text-slate-700 mb-2">
              Contato
            </label>
            <input
              id="contact-filter"
              type="search"
              value={contactFilter}
              onChange={handleContactChange}
              disabled={requiresAgent && !selectedAgentId}
              placeholder="Buscar por telefone..."
              className="field-input w-full disabled:opacity-60"
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-red-700">{error}</p>
          <button
            type="button"
            onClick={() => loadItems(meta.page)}
            className="btn-secondary text-sm px-3 py-2"
          >
            Tentar novamente
          </button>
        </div>
      )}

      {actionError && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm text-red-700">{actionError}</p>
        </div>
      )}

      {loading ? (
        <div className="surface-card px-4 py-12 text-center">
          <p className="text-content-muted text-sm">Carregando agendamentos...</p>
        </div>
      ) : items.length === 0 && !error ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-12 text-center">
          <p className="text-content-primary font-medium">{emptyMessage.title}</p>
          <p className="text-content-muted text-sm mt-2">{emptyMessage.description}</p>
        </div>
      ) : (
        <>
          <ScheduledMessagesTable
            mode={mode}
            items={items}
            agentId={selectedAgentId}
            contactsById={contactsById}
            showAgentColumn={showAgentColumn}
            onCancelSuccess={() => {
              setActionError('');
              loadItems(meta.page);
            }}
            onCancelError={setActionError}
            onDeleteSuccess={() => {
              setActionError('');
              loadItems(meta.page);
            }}
            onDeleteError={setActionError}
            onRetrySuccess={() => {
              setActionError('');
              loadItems(meta.page);
            }}
            onRetryError={setActionError}
            onResendSuccess={() => {
              setActionError('');
              loadItems(meta.page);
            }}
            onResendError={setActionError}
            onForceDispatchSuccess={() => {
              setActionError('');
              loadItems(meta.page);
            }}
            onForceDispatchError={setActionError}
            onSendTemplateSuccess={() => {
              setActionError('');
              loadItems(meta.page);
            }}
            onSendTemplateError={setActionError}
          />

          {hasPagination && (
            <div className="flex items-center justify-between gap-3 pt-2">
              <p className="text-xs text-content-subtle">
                Página {meta.page} de {totalPages}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={meta.page <= 1 || loading}
                  onClick={() => loadItems(meta.page - 1)}
                  className="btn-secondary text-xs px-3 py-2 disabled:opacity-40"
                >
                  Anterior
                </button>
                <button
                  type="button"
                  disabled={meta.page >= totalPages || loading}
                  onClick={() => loadItems(meta.page + 1)}
                  className="btn-secondary text-xs px-3 py-2 disabled:opacity-40"
                >
                  Próxima
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ScheduledMessagesPage;
